import { PROMETHEUS_CONFIG } from '@/shared/config/prometheus-config';

export const PROMETHEUS_NAMESPACE = PROMETHEUS_CONFIG.NAMESPACE;
const EPHEMERAL_STORAGE_RESOURCE_PATTERN = 'ephemeral_storage|ephemeral-storage';
const API_SERVER_ERROR_RATE_TOTAL_QUERY =
  '100 * sum(rate(apiserver_request_total{code=~"5.."}[5m])) / clamp_min(sum(rate(apiserver_request_total[5m])), 1e-9)';
const API_SERVER_ERROR_RATE_BY_POD_QUERY =
  '100 * sum by (pod) (rate(apiserver_request_total{code=~"5..", pod!=""}[5m])) / clamp_min(sum by (pod) (rate(apiserver_request_total{pod!=""}[5m])), 1e-9)';

export const PROMETHEUS_QUERIES = {
  CPU: {
    TOTAL: `sum(node_namespace_pod_container:container_cpu_usage_seconds_total:sum_irate{namespace="${PROMETHEUS_NAMESPACE}"})`,
    BY_POD: `sum(node_namespace_pod_container:container_cpu_usage_seconds_total:sum_irate{namespace="${PROMETHEUS_NAMESPACE}"}) by (pod)`,
    HISTORY: `sum(node_namespace_pod_container:container_cpu_usage_seconds_total:sum_irate{namespace="${PROMETHEUS_NAMESPACE}"})`,
    LIMIT: `sum(kube_pod_container_resource_limits{namespace="${PROMETHEUS_NAMESPACE}", resource="cpu", unit="core"})`,
    REQUEST: `sum(kube_pod_container_resource_requests{namespace="${PROMETHEUS_NAMESPACE}", resource="cpu", unit="core"})`,
  },
  MEMORY: {
    TOTAL: `sum(node_namespace_pod_container:container_memory_working_set_bytes{namespace="${PROMETHEUS_NAMESPACE}"})`,
    BY_POD: `sum(node_namespace_pod_container:container_memory_working_set_bytes{namespace="${PROMETHEUS_NAMESPACE}"}) by (pod)`,
    HISTORY: `sum(node_namespace_pod_container:container_memory_working_set_bytes{namespace="${PROMETHEUS_NAMESPACE}"})`,
    LIMIT: `sum(kube_pod_container_resource_limits{namespace="${PROMETHEUS_NAMESPACE}", resource="memory", unit="byte"})`,
    REQUEST: `sum(kube_pod_container_resource_requests{namespace="${PROMETHEUS_NAMESPACE}", resource="memory", unit="byte"})`,
  },
  NETWORK: {
    RECEIVE: `sum(irate(container_network_receive_bytes_total{namespace="${PROMETHEUS_NAMESPACE}"}[5m]))`,
    TRANSMIT: `sum(irate(container_network_transmit_bytes_total{namespace="${PROMETHEUS_NAMESPACE}"}[5m]))`,
    RECEIVE_BY_POD: `sum(irate(container_network_receive_bytes_total{namespace="${PROMETHEUS_NAMESPACE}"}[5m])) by (pod)`,
    TRANSMIT_BY_POD: `sum(irate(container_network_transmit_bytes_total{namespace="${PROMETHEUS_NAMESPACE}"}[5m])) by (pod)`,
    RECEIVE_HISTORY: `sum(irate(container_network_receive_bytes_total{namespace="${PROMETHEUS_NAMESPACE}"}[5m]))`,
    TRANSMIT_HISTORY: `sum(irate(container_network_transmit_bytes_total{namespace="${PROMETHEUS_NAMESPACE}"}[5m]))`,
  },
  DISK: {
    READ: `sum(irate(container_fs_reads_bytes_total{namespace="${PROMETHEUS_NAMESPACE}"}[5m]))`,
    WRITE: `sum(irate(container_fs_writes_bytes_total{namespace="${PROMETHEUS_NAMESPACE}"}[5m]))`,
    READ_BY_POD: `sum(irate(container_fs_reads_bytes_total{namespace="${PROMETHEUS_NAMESPACE}"}[5m])) by (pod)`,
    WRITE_BY_POD: `sum(irate(container_fs_writes_bytes_total{namespace="${PROMETHEUS_NAMESPACE}"}[5m])) by (pod)`,
    READ_HISTORY: `sum(irate(container_fs_reads_bytes_total{namespace="${PROMETHEUS_NAMESPACE}"}[5m]))`,
    WRITE_HISTORY: `sum(irate(container_fs_writes_bytes_total{namespace="${PROMETHEUS_NAMESPACE}"}[5m]))`,
  },
  STORAGE: {
    TOTAL: `sum(container_fs_usage_bytes{namespace="${PROMETHEUS_NAMESPACE}"})`,
    BY_POD: `sum(container_fs_usage_bytes{namespace="${PROMETHEUS_NAMESPACE}"}) by (pod)`,
    HISTORY: `sum(container_fs_usage_bytes{namespace="${PROMETHEUS_NAMESPACE}"})`,
    REQUEST: `sum(kube_pod_container_resource_requests{namespace="${PROMETHEUS_NAMESPACE}", resource=~"${EPHEMERAL_STORAGE_RESOURCE_PATTERN}", unit="byte"})`,
    LIMIT: `sum(kube_pod_container_resource_limits{namespace="${PROMETHEUS_NAMESPACE}", resource=~"${EPHEMERAL_STORAGE_RESOURCE_PATTERN}", unit="byte"})`,
  },
  ERROR_RATE: {
    TOTAL: API_SERVER_ERROR_RATE_TOTAL_QUERY,
    BY_POD: API_SERVER_ERROR_RATE_BY_POD_QUERY,
    HISTORY: API_SERVER_ERROR_RATE_TOTAL_QUERY,
  },
  POD_OVERVIEW: {
    CPU_USAGE: `sum(node_namespace_pod_container:container_cpu_usage_seconds_total:sum_irate{namespace="${PROMETHEUS_NAMESPACE}"}) by (pod)`,
    CPU_REQUESTS: `sum(cluster:namespace:pod_cpu:active:kube_pod_container_resource_requests{namespace="${PROMETHEUS_NAMESPACE}"}) by (pod)`,
    CPU_LIMITS: `sum(cluster:namespace:pod_cpu:active:kube_pod_container_resource_limits{namespace="${PROMETHEUS_NAMESPACE}"}) by (pod)`,
    MEMORY_USAGE: `sum(container_memory_working_set_bytes{job="kubelet", metrics_path="/metrics/cadvisor", namespace="${PROMETHEUS_NAMESPACE}", container!="", image!=""}) by (pod)`,
    MEMORY_REQUESTS: `sum(cluster:namespace:pod_memory:active:kube_pod_container_resource_requests{namespace="${PROMETHEUS_NAMESPACE}"}) by (pod)`,
    MEMORY_LIMITS: `sum(cluster:namespace:pod_memory:active:kube_pod_container_resource_limits{namespace="${PROMETHEUS_NAMESPACE}"}) by (pod)`,
    MEMORY_RSS: `sum(container_memory_rss{job="kubelet", metrics_path="/metrics/cadvisor", namespace="${PROMETHEUS_NAMESPACE}", container!=""}) by (pod)`,
    MEMORY_CACHE: `sum(container_memory_cache{job="kubelet", metrics_path="/metrics/cadvisor", namespace="${PROMETHEUS_NAMESPACE}", container!=""}) by (pod)`,
    MEMORY_SWAP: `sum(container_memory_swap{job="kubelet", metrics_path="/metrics/cadvisor", namespace="${PROMETHEUS_NAMESPACE}", container!=""}) by (pod)`,
  },
} as const;

export const QUERY_INTERVALS = {
  INSTANT: 'instant',
  RANGE_1H: 3600,
  RANGE_6H: 21600,
  RANGE_24H: 86400,
  STEP_1M: 60,
  STEP_5M: 300,
} as const;
